/**
 * Copyright IBM Corp. 2022, 2026
 */

'use client'

// This is largely a copy of the existing `Tabs` Materia block.
import { ReactNode } from 'react'
import { m } from 'motion/react'
import { Tabs, useTabsContext } from '@hashicorp/mds-react/components'
import AgendaPanelWrapper from 'components/materia/conference-agenda/agenda-panel'
import { Layout } from 'components/materia/layout'
import { type TTracks } from 'app/[locale]/conferences/[conference]/agenda/[date]/page'
import s from './style.module.css'
import { TabsHandler } from '../tabs/components/tabs-handler'

const ConferenceAgenda = ({
	appearance,
	agendaItemsByTrack,
}: {
	appearance: string
	agendaItemsByTrack: TTracks
}) => {
	return (
		<Layout layout={{ appearance }}>
			<div className={s.root}>
				<TabsHandler
					updateUrlOnChange
					panels={agendaItemsByTrack.map((track) => ({ name: track.name }))}
				>
					<div className={s.panels}>
						{agendaItemsByTrack.map((track, id) => {
							return (
								<AnimatedPanel key={id} index={id}>
									<AgendaPanelWrapper track={track} />
								</AnimatedPanel>
							)
						})}
					</div>
				</TabsHandler>
			</div>
		</Layout>
	)
}

const variants = {
	active: {
		opacity: 1,
		display: 'block',
		transition: {
			opacity: {
				duration: 0.3,
				delay: 0.3,
			},
		},
	},
	inactive: {
		opacity: 0,
		transition: {
			duration: 0.3,
		},
		transitionEnd: {
			display: 'none',
		},
	},
}

interface AnimatedPanelProps {
	index: number
	children: ReactNode
}

const AnimatedPanel = ({ index, children }: AnimatedPanelProps) => {
	const { selectedTabIndex } = useTabsContext()
	const isActive = selectedTabIndex === index

	return (
		<m.div
			key={index}
			className={s.panelWrapper}
			variants={variants}
			style={{ opacity: 0 }}
			initial={index === 0 ? 'visible' : 'hidden'}
			animate={isActive ? 'active' : 'inactive'}
			// @see https://github.com/WICG/inert/issues/58
			// @see https://github.com/facebook/react/pull/24730
			// @see https://github.com/DefinitelyTyped/DefinitelyTyped/pull/60822
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			inert={!isActive ? 'true' : undefined}
			forceMount
		>
			<Tabs.Panel className={s.panel}>
				<div className={s.panel}>{children}</div>
			</Tabs.Panel>
		</m.div>
	)
}

export default ConferenceAgenda
